import React, { useRef, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { Stack, StackProps, useKeyboardListNavigation } from '@moda/om';
import { SizeInventoryFragment } from '../../generated/types';
import { SizeSelectorItem } from './SizeSelectorItem';
import './SizeSelectorList.scss';

type Props = Omit<StackProps, 'space' | 'onSelect'> & {
  id?: string;
  sizes: SizeInventoryFragment[];
  sizeEquivalents: string[][];
  disableSizeMapping: boolean;
  selectedSize: SizeInventoryFragment | null;
  onSelect: (size: SizeInventoryFragment) => void;
  onClose: () => void;
  'aria-label'?: string;
};

export const SizeSelectorList: React.FC<Props> = ({
  id,
  className,
  sizes,
  sizeEquivalents,
  disableSizeMapping,
  selectedSize,
  onSelect,
  onClose,
  'aria-label': ariaLabel,
  ...rest
}) => {
  const listRef = useRef<HTMLDivElement>(null);

  const { index: focusedIndex } = useKeyboardListNavigation({
    ref: listRef,
    list: sizes,
    defaultSelected: selectedSize ?? undefined,
    onEnter: ({ element }) => onSelect(element),
    extractValue: item => item.size ?? ''
  });

  // The list overlays the page, so focusing it shouldn't scroll the window.
  useEffect(() => {
    listRef.current?.focus({ preventScroll: true });
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    },
    [onClose]
  );

  const activeDescendantId = sizes[focusedIndex]?.size
    ? `${id}-option-${sizes[focusedIndex].size}`
    : undefined;

  return (
    <div
      ref={listRef}
      id={id}
      role="listbox"
      aria-label={ariaLabel}
      aria-activedescendant={activeDescendantId}
      tabIndex={0}
      className={classNames('SizeSelectorList', className)}
      onKeyDown={handleKeyDown}
    >
      <Stack space={0} {...rest}>
        {sizes.map((size, index) => (
          <SizeSelectorItem
            id={`${id}-option-${size.size}`}
            key={size.size}
            className="SizeSelectorList__item"
            data-test-id="size-selector-item"
            data-test-size={size.size}
            data-test-sellable={size.canSell}
            data-test-available={size.canSell}
            data-test-selected={selectedSize?.size == size.size}
            size={size}
            sizeEquivalents={sizeEquivalents}
            disableSizeMapping={disableSizeMapping}
            selected={selectedSize?.size === size.size}
            focused={focusedIndex === index}
            onClick={() => onSelect(size)}
          />
        ))}
      </Stack>
    </div>
  );
};
